import {CancelToken} from 'axios';
import {saveAs} from 'file-saver';

import {apiClient} from 'core/apiClient';
import {FormattedLabelsForm} from 'types/orders';

export interface CodeExportJob {
    jobId: string;
    status: 'PENDING' | 'PROCESSING' | 'DONE' | 'FAILED';
    filename?: string | null;
    error?: string | null;
}

export type CodeExportRequest = Pick<FormattedLabelsForm, 'orderId' | 'gtin' | 'format'> &
    Partial<Pick<FormattedLabelsForm, 'pageFormat' | 'showHri'>>;

export async function createCodeExport(payload: CodeExportRequest, cancelToken: CancelToken) {
    return (
        await apiClient.post<CodeExportJob>('/service/printing/export/async', payload, {
            cancelToken,
        })
    ).data;
}

export async function getCodeExport(jobId: string, cancelToken: CancelToken) {
    return (
        await apiClient.get<CodeExportJob>(
            '/service/printing/export/async/' + encodeURIComponent(jobId),
            {cancelToken},
        )
    ).data;
}

export async function downloadCodeExport(
    job: CodeExportJob,
    payload: CodeExportRequest,
    cancelToken: CancelToken,
) {
    const response = await apiClient.get<Blob>(
        '/service/printing/export/async/' + encodeURIComponent(job.jobId) + '/download',
        {
            responseType: 'blob',
            cancelToken,
        },
    );
    if (response.data.type.includes('json')) {
        const error = JSON.parse(await response.data.text());
        throw new Error(error.errors?.[0]?.message || error.message || 'Export failed');
    }
    const disposition = String(response.headers['content-disposition'] || '');
    const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
    const plain = /filename="?([^";]+)"?/i.exec(disposition);
    let filename =
        job.filename ||
        `marking_codes_${payload.orderId}_${payload.gtin}.${payload.format.toLowerCase()}`;
    if (encoded) {
        try {
            filename = decodeURIComponent(encoded[1]);
        } catch {
            /* Keep the server/fallback filename. */
        }
    } else if (plain) [, filename] = plain;
    cancelToken.throwIfRequested();
    saveAs(response.data, filename);
}
